import { useEffect, useMemo, useState } from "react";
import {
    ConfigProvider, Select, Card, Table, Tag, Space, Button, Row, Col, Statistic,
    Alert, Tooltip, Typography, Empty, InputNumber, Switch, Radio, Progress,
} from "antd";
import frFR from "antd/locale/fr_FR";
import {
    ReloadOutlined, PercentageOutlined, PieChartOutlined, BankOutlined,
    TeamOutlined, InfoCircleOutlined, ArrowLeftOutlined, ExperimentOutlined,
    FileExcelOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import SideBar from "../../../../components/SideBar";
import NavBar from "../../../../components/NavBar";
import Footer from "../../../../components/Footer";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { displayNumbers } from "../../../../variables/numbersFunctions";

const { Text, Title } = Typography;
const PRIMARY = "#242166";

// Assiette du chiffre d'affaires N-1 servant de base à la part variable.
const ASSIETTES = [
    { value: "NETTE", label: "Prime nette", champ: "primeNetteNet" },
    { value: "TTC", label: "Prime TTC", champ: "montantNet" },
];

/**
 * Écran de SIMULATION d'une clé de répartition (aucune écriture en base).
 * Règle : la clé de chaque compagnie active se compose de
 *   - une PART FIXE (par défaut 50 %) répartie ÉQUITABLEMENT entre les compagnies actives ;
 *   - une PART VARIABLE (par défaut 50 %) répartie AU PRORATA du chiffre d'affaires N-1.
 * clé_i = partFixe%/nbActives + partVar% × (CA_i / ΣCA)
 */
const SimulationCleRepartition = () => {
    const navigate = useNavigate();

    const [compagnies, setCompagnies] = useState([]);
    const [exercices, setExercices] = useState([]);
    const [loadingCA, setLoadingCA] = useState(false);

    const [exercice, setExercice] = useState(null);      // année N (la clé s'appliquera à N)
    const [assiette, setAssiette] = useState("NETTE");
    const [partFixe, setPartFixe] = useState(50);         // % ; part variable = 100 - partFixe
    const partVar = Math.max(0, Math.min(100, 100 - partFixe));

    // Lignes de simulation : { code, nom, active, ca }
    const [lignes, setLignes] = useState([]);

    const anneeCA = exercice ? exercice - 1 : null;

    useEffect(() => { chargerDonneesInitiales(); }, []);
    useEffect(() => { if (exercice) chargerChiffresAffaires(); /* eslint-disable-next-line */ }, [exercice, assiette]);

    const chargerDonneesInitiales = async () => {
        try {
            const [compRes, exRes] = await Promise.all([
                axiosInstance.get("/api/organisme/assurance"),
                axiosInstance.get("/api/stats/production/exercices"),
            ]);
            const comps = compRes.data || [];
            setCompagnies(comps);
            const exs = (exRes.data || []).map((e) => e.exercice).filter((y) => y != null).sort((a, b) => b - a);
            setExercices(exs);
            const currentYear = exs.length ? exs[0] : null;
            setExercice(currentYear);
            // Init des lignes (CA à 0, seront pré-remplies au chargement des CA).
            setLignes(comps.map((c) => ({ code: c.code, nom: c.raisonSociale || c.code, active: true, ca: 0 })));
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des données");
        }
    };

    // Récupère la production N-1 et agrège le CA par compagnie (organismeCode).
    const chargerChiffresAffaires = async () => {
        if (!exercice) return;
        setLoadingCA(true);
        try {
            const champ = ASSIETTES.find((a) => a.value === assiette)?.champ || "primeNetteNet";
            const res = await axiosInstance.get("/api/stats/production", { params: { exercice: exercice - 1 } });
            const rows = res.data || [];
            const caParCompagnie = {};
            rows.forEach((r) => {
                const code = r.organismeCode;
                if (!code) return;
                caParCompagnie[code] = (caParCompagnie[code] || 0) + (Number(r[champ]) || 0);
            });
            setLignes((prev) => prev.map((l) => {
                const ca = Math.max(0, Math.round(caParCompagnie[l.code] || 0));
                return { ...l, ca, active: ca > 0 };
            }));
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des chiffres d'affaires");
        } finally {
            setLoadingCA(false);
        }
    };

    const majLigne = (code, patch) =>
        setLignes((prev) => prev.map((l) => (l.code === code ? { ...l, ...patch } : l)));

    // Calcul de la répartition simulée (arrondi 2 déc. + report du résidu sur la plus grosse part → Σ = 100).
    const calcul = useMemo(() => {
        const actives = lignes.filter((l) => l.active);
        const nbActives = actives.length;
        const totalCA = actives.reduce((s, l) => s + (Number(l.ca) || 0), 0);

        const details = lignes.map((l) => {
            if (!l.active || nbActives === 0) {
                return { ...l, poidsCA: 0, ptsFixe: 0, ptsVar: 0, cle: 0 };
            }
            const poidsCA = totalCA > 0 ? (Number(l.ca) || 0) / totalCA : 1 / nbActives;
            const ptsFixe = partFixe / nbActives;
            const ptsVar = partVar * poidsCA;
            const cle = ptsFixe + ptsVar;
            return { ...l, poidsCA, ptsFixe, ptsVar, cle };
        });

        // Arrondi au centième + résidu sur la plus grosse clé pour un total EXACT de 100.
        const arrondies = details.map((d) => ({ ...d, cleArr: Math.round(d.cle * 100) / 100 }));
        const actArr = arrondies.filter((d) => d.active);
        const sommeArr = actArr.reduce((s, d) => s + d.cleArr, 0);
        if (actArr.length) {
            const cible = partFixe + partVar; // 100 en principe
            const residu = Math.round((cible - sommeArr) * 100) / 100;
            if (residu !== 0) {
                const plusGrosse = actArr.reduce((m, d) => (d.cleArr > m.cleArr ? d : m), actArr[0]);
                plusGrosse.cleArr = Math.round((plusGrosse.cleArr + residu) * 100) / 100;
            }
        }
        const totalCle = arrondies.reduce((s, d) => s + (d.active ? d.cleArr : 0), 0);
        return { details: arrondies, nbActives, totalCA, totalCle: Math.round(totalCle * 100) / 100 };
    }, [lignes, partFixe, partVar]);

    const exporterExcel = () => {
        const data = calcul.details
            .filter((d) => d.active)
            .map((d) => ({
                "Compagnie": d.nom,
                "Code": d.code,
                [`Chiffre d'affaires ${anneeCA ?? "N-1"}`]: Math.round(d.ca),
                "Poids CA (%)": Math.round(d.poidsCA * 10000) / 100,
                [`Part fixe (${partFixe}%)`]: Math.round(d.ptsFixe * 100) / 100,
                [`Part variable (${partVar}%)`]: Math.round(d.ptsVar * 100) / 100,
                "Clé simulée (%)": d.cleArr,
            }));
        if (!data.length) { toast.warning("Aucune compagnie active à exporter"); return; }
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Simulation clés");
        XLSX.writeFile(wb, `Simulation_cle_repartition_${exercice || ""}.xlsx`);
    };

    const colonnes = [
        {
            title: "Compagnie", key: "compagnie",
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 600, color: r.active ? PRIMARY : "#aaa" }}>{r.nom}</div>
                    <div style={{ fontSize: 12, color: "#999" }}>{r.code}</div>
                </div>
            ),
        },
        {
            title: "Active", key: "active", align: "center", width: 90,
            render: (_, r) => (
                <Switch checked={r.active} onChange={(v) => majLigne(r.code, { active: v })} size="small" />
            ),
        },
        {
            title: <span>Chiffre d'affaires <Tag color="blue" style={{ marginLeft: 4 }}>{anneeCA ?? "N-1"}</Tag></span>,
            key: "ca", align: "right", width: 220,
            render: (_, r) => (
                <InputNumber
                    value={r.ca}
                    onChange={(v) => majLigne(r.code, { ca: v || 0 })}
                    disabled={!r.active}
                    min={0} step={1000} style={{ width: "100%" }}
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                    parser={(v) => (v || "").replace(/\s/g, "")}
                />
            ),
        },
        {
            title: "Poids CA", key: "poids", align: "center", width: 130,
            render: (_, r) => r.active
                ? <Progress percent={Math.round(r.poidsCA * 1000) / 10} size="small" strokeColor={PRIMARY}
                    format={(p) => `${p}%`} />
                : <Text type="secondary">—</Text>,
        },
        {
            title: `Part fixe (${partFixe}%)`, key: "ptsFixe", align: "right", width: 120,
            render: (_, r) => r.active ? <span>{(Math.round(r.ptsFixe * 100) / 100).toFixed(2)} pts</span> : "—",
        },
        {
            title: `Part variable (${partVar}%)`, key: "ptsVar", align: "right", width: 130,
            render: (_, r) => r.active ? <span>{(Math.round(r.ptsVar * 100) / 100).toFixed(2)} pts</span> : "—",
        },
        {
            title: "Clé simulée", key: "cle", align: "center", width: 130, fixed: "right",
            render: (_, r) => r.active
                ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 15, color: PRIMARY }}>
                    <PercentageOutlined /> {r.cleArr.toFixed(2)}%
                </span>
                : <Text type="secondary">—</Text>,
        },
    ];

    const cardStyle = { borderRadius: 14, border: "1px solid #eef0f4", boxShadow: "0 2px 10px rgba(36,33,102,0.05)" };
    const totalOk = Math.abs(calcul.totalCle - 100) < 0.005;

    return (
        <ConfigProvider locale={frFR}>
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                    <SideBar currentMenu={"AUTRES_PARAMETRES"} />
                    <div className="layout-page">
                        <NavBar />
                        <div className="content-wrapper" style={{ background: "#f6f7fb" }}>
                            <div className="container-xxl flex-grow-1 container-p-y">

                                {/* En-tête */}
                                <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #1890ff 100%)`, borderRadius: 14, padding: "18px 22px", marginBottom: 18, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                                    <div>
                                        <Title level={4} style={{ margin: 0, color: "#fff" }}>
                                            <ExperimentOutlined style={{ marginRight: 8 }} />Simulation de clé de répartition
                                        </Title>
                                        <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                            Part fixe équitable + part variable au prorata du chiffre d'affaires N-1 (aucune écriture en base)
                                        </Text>
                                    </div>
                                    <Space wrap>
                                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/autre-parametres/cle-de-repartition/v2")}
                                            style={{ borderRadius: 8, fontWeight: 600 }}>Retour aux clés</Button>
                                        <Button icon={<FileExcelOutlined />} onClick={exporterExcel}
                                            style={{ background: "#fff", color: "#059669", borderRadius: 8, fontWeight: 700, border: "none" }}>Exporter</Button>
                                    </Space>
                                </div>

                                {/* Règle */}
                                <Alert type="info" showIcon icon={<InfoCircleOutlined />} style={{ marginBottom: 18, borderRadius: 12 }}
                                    message={<span><strong>Clé = part fixe / nombre de compagnies actives + part variable × (CA de la compagnie / CA total)</strong></span>}
                                    description={`Le chiffre d'affaires est celui de l'exercice ${anneeCA ?? "N-1"} (assiette ${ASSIETTES.find((a) => a.value === assiette)?.label.toLowerCase()}). Vous pouvez ajuster manuellement les CA, activer/désactiver des compagnies et modifier le partage fixe/variable pour tester différents scénarios.`} />

                                {/* Paramètres */}
                                <Card style={{ ...cardStyle, marginBottom: 18 }} bodyStyle={{ padding: 16 }}>
                                    <Row gutter={[16, 16]} align="middle">
                                        <Col xs={24} md={6}>
                                            <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Exercice de la clé (N)</div>
                                            <Select style={{ width: "100%" }} value={exercice || undefined}
                                                onChange={(v) => setExercice(v)}
                                                options={exercices.map((y) => ({ value: y, label: `${y}  (CA ${y - 1})` }))}
                                                placeholder="Exercice" />
                                        </Col>
                                        <Col xs={24} md={7}>
                                            <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Assiette du chiffre d'affaires</div>
                                            <Radio.Group value={assiette} onChange={(e) => setAssiette(e.target.value)} optionType="button" buttonStyle="solid">
                                                {ASSIETTES.map((a) => <Radio.Button key={a.value} value={a.value}>{a.label}</Radio.Button>)}
                                            </Radio.Group>
                                        </Col>
                                        <Col xs={24} md={6}>
                                            <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Part fixe (équitable)</div>
                                            <InputNumber min={0} max={100} step={5} value={partFixe}
                                                onChange={(v) => setPartFixe(Math.max(0, Math.min(100, v ?? 0)))}
                                                addonAfter="%" style={{ width: "100%" }} />
                                        </Col>
                                        <Col xs={24} md={5}>
                                            <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Part variable (prorata CA)</div>
                                            <InputNumber value={partVar} disabled addonAfter="%" style={{ width: "100%" }} />
                                        </Col>
                                    </Row>
                                    <div style={{ marginTop: 12 }}>
                                        <Tooltip title="Recharger les CA depuis la production N-1">
                                            <Button icon={<ReloadOutlined />} onClick={chargerChiffresAffaires} loading={loadingCA}>
                                                Recharger les chiffres d'affaires {anneeCA ?? ""}
                                            </Button>
                                        </Tooltip>
                                    </div>
                                </Card>

                                {/* Synthèse */}
                                <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
                                    {[
                                        { t: "Compagnies actives", v: calcul.nbActives, icon: <TeamOutlined />, c: PRIMARY, suffix: "" },
                                        { t: `CA total ${anneeCA ?? ""}`, v: calcul.totalCA, icon: <BankOutlined />, c: "#0891b2", suffix: " F", isMoney: true },
                                        { t: "Partage fixe / variable", v: `${partFixe} / ${partVar}`, icon: <PieChartOutlined />, c: "#d97706", suffix: " %", isText: true },
                                        { t: "Total des clés", v: calcul.totalCle, precision: 2, icon: <PercentageOutlined />, c: totalOk ? "#16a34a" : "#dc2626", suffix: " %" },
                                    ].map((s) => (
                                        <Col xs={12} lg={6} key={s.t}>
                                            <Card style={cardStyle} bodyStyle={{ padding: 16, display: "flex", alignItems: "center", gap: 12 }}>
                                                <span style={{ width: 42, height: 42, borderRadius: 11, background: `${s.c}14`, color: s.c, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{s.icon}</span>
                                                {s.isMoney
                                                    ? <div><div style={{ fontSize: 13, color: "#888" }}>{s.t}</div><div style={{ color: s.c, fontWeight: 700, fontSize: 20 }}>{displayNumbers(Math.round(s.v))} F</div></div>
                                                    : s.isText
                                                        ? <div><div style={{ fontSize: 13, color: "#888" }}>{s.t}</div><div style={{ color: s.c, fontWeight: 700, fontSize: 22 }}>{s.v} %</div></div>
                                                        : <Statistic title={s.t} value={s.v} precision={s.precision} suffix={s.suffix}
                                                            valueStyle={{ color: s.c, fontWeight: 700, fontSize: 22 }} />}
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>

                                {!totalOk && calcul.nbActives > 0 && (
                                    <Alert type="warning" showIcon style={{ marginBottom: 12, borderRadius: 10 }}
                                        message={`Le total des clés est de ${calcul.totalCle.toFixed(2)} % (attendu 100 %).`} />
                                )}

                                {/* Tableau de simulation */}
                                <Card style={cardStyle} bodyStyle={{ padding: 8 }}>
                                    <Table
                                        rowKey="code"
                                        loading={loadingCA}
                                        columns={colonnes}
                                        dataSource={calcul.details}
                                        size="small"
                                        pagination={false}
                                        scroll={{ x: "max-content" }}
                                        locale={{ emptyText: <Empty description="Aucune compagnie" /> }}
                                        rowClassName={(r) => (r.active ? "" : "row-inactive")}
                                        summary={() => {
                                            const actives = calcul.details.filter((d) => d.active);
                                            return (
                                                <Table.Summary fixed>
                                                    <Table.Summary.Row style={{ background: "#fafafa", fontWeight: 700 }}>
                                                        <Table.Summary.Cell index={0}>Total ({actives.length} active(s))</Table.Summary.Cell>
                                                        <Table.Summary.Cell index={1} />
                                                        <Table.Summary.Cell index={2} align="right">{displayNumbers(Math.round(calcul.totalCA))} F</Table.Summary.Cell>
                                                        <Table.Summary.Cell index={3} align="center">100%</Table.Summary.Cell>
                                                        <Table.Summary.Cell index={4} align="right">{partFixe.toFixed(2)} pts</Table.Summary.Cell>
                                                        <Table.Summary.Cell index={5} align="right">{partVar.toFixed(2)} pts</Table.Summary.Cell>
                                                        <Table.Summary.Cell index={6} align="center">
                                                            <span style={{ color: totalOk ? "#16a34a" : "#dc2626", fontWeight: 800 }}>{calcul.totalCle.toFixed(2)}%</span>
                                                        </Table.Summary.Cell>
                                                    </Table.Summary.Row>
                                                </Table.Summary>
                                            );
                                        }}
                                    />
                                </Card>

                            </div>
                            <Footer />
                            <div className="content-backdrop fade"></div>
                        </div>
                    </div>
                </div>
                <div className="layout-overlay layout-menu-toggle"></div>
                <div className="drag-target"></div>
            </div>
        </ConfigProvider>
    );
};

export default SimulationCleRepartition;
